import {
  Box,
  Container,
  Stack,
  Typography,
  Paper,
  CircularProgress,
  Alert,
  Button,
  Chip,
  Divider,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Badge,
  Card,
  CardContent,
  Avatar,
  Link as MuiLink,
  LinearProgress,
  Tabs,
  Tab,
} from '@mui/material';
import { format } from 'date-fns';
import React from 'react';
import ReactMarkdown, { Components } from 'react-markdown';
import { useHistory, useParams } from 'react-router-dom';

import { Communication } from '@/types/communication';

import { useDistanceCalculationStatus } from '@/api/endpoints/vendor/useDistanceCalculationStatus';
import { useGetCommunicationHistory } from '@/api/endpoints/vendor/useGetCommunicationHistory';
import {
  useGetSearchHistory,
  VendorSearchHistory,
  VendorDetails,
} from '@/api/endpoints/vendor/useGetSearchHistory';
import { useTriggerDistanceCalculation } from '@/api/endpoints/vendor/useTriggerDistanceCalculation';
import { ArrowLeftIcon } from '@/assets/icons/ArrowLeftIcon';
import { CheckCircleIcon } from '@/assets/icons/CheckCircleIcon';
import { ChevronDownIcon } from '@/assets/icons/ChevronDownIcon';
import { Globe01Icon } from '@/assets/icons/Globe01Icon';
import { Headphones01Icon } from '@/assets/icons/Headphones01Icon';
import { Loading01Icon } from '@/assets/icons/Loading01Icon';
import { Mail01Icon } from '@/assets/icons/Mail01Icon';
import { MarkerPin01Icon } from '@/assets/icons/MarkerPin01Icon';
import { MessageChatSquareIcon } from '@/assets/icons/MessageChatSquareIcon';
import { Phone01Icon } from '@/assets/icons/Phone01Icon';
import { PhoneIcon } from '@/assets/icons/PhoneIcon';
import { PMPRoutes } from '@/config/routes';

import CustomerRequirementsDisplay from '@/components/vendor/CustomerRequirements';

// Define markdown components outside render to avoid ESLint warnings
const markdownComponents: Components = {
  p: ({ children }) => (
    <Typography variant="body2" sx={{ mb: 1 }}>
      {children}
    </Typography>
  ),
  h1: ({ children }) => (
    <Typography variant="h6" sx={{ mb: 1.5, fontWeight: 'bold' }}>
      {children}
    </Typography>
  ),
  h2: ({ children }) => (
    <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 'bold' }}>
      {children}
    </Typography>
  ),
  h3: ({ children }) => (
    <Typography variant="subtitle2" sx={{ mb: 0.5, fontWeight: 'bold' }}>
      {children}
    </Typography>
  ),
  ul: ({ children }) => (
    <Typography component="ul" sx={{ mb: 1, pl: 2 }}>
      {children}
    </Typography>
  ),
  ol: ({ children }) => (
    <Typography component="ol" sx={{ mb: 1, pl: 2 }}>
      {children}
    </Typography>
  ),
  li: ({ children }) => (
    <Typography component="li" variant="body2" sx={{ mb: 0.25 }}>
      {children}
    </Typography>
  ),
  strong: ({ children }) => (
    <Typography component="strong" sx={{ fontWeight: 'bold' }}>
      {children}
    </Typography>
  ),
};

// Communication Item Component
const CommunicationItem: React.FC<{ communication: Communication; index: number }> = ({
  communication,
  index,
}) => {
  const formatPhoneNumber = (phone: string) => {
    const cleaned = phone.replace(/\D/g, '');
    if (cleaned.length === 10) {
      return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
    }
    if (cleaned.length === 11 && cleaned[0] === '1') {
      return `+1 (${cleaned.slice(1, 4)}) ${cleaned.slice(4, 7)}-${cleaned.slice(7)}`;
    }
    return phone;
  };

  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
    case 'completed':
    case 'success':
      return 'success';
    case 'failed':
    case 'error':
      return 'error';
    case 'voicemail':
      return 'warning';
    default:
      return 'default';
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status.toLowerCase()) {
    case 'completed':
      return 'Connected';
    case 'sms_sent':
      return 'SMS Sent';
    case 'failed':
      return 'Failed';
    case 'error':
      return 'Error';
    case 'voicemail':
      return 'Voicemail';
    default:
      return status;
    }
  };

  const formatMessageTimestamp = (timestamp: string) => {
    try {
      const date = new Date(timestamp);
      return format(date, 'MMM d, h:mm a');
    } catch {
      return '';
    }
  };

  const getCommunicationTypeIcon = () => {
    if (communication.type === 'call') {
      return <Phone01Icon width={18} height={18} />;
    }
    return <MessageChatSquareIcon width={18} height={18} />;
  };

  const getCommunicationTypeLabel = () => {
    if (communication.type === 'call') {
      return 'AI Call';
    }
    return 'SMS Conversation';
  };

  return (
    <Accordion
      key={communication.id}
      defaultExpanded={index === 0}
      sx={{
        borderRadius: 0,
        '&:before': { display: 'none' },
        '&.Mui-expanded': {
          bgcolor: 'background.paper',
        },
      }}
      elevation={0}
    >
      <AccordionSummary
        expandIcon={<ChevronDownIcon width={18} height={18} />}
        sx={{ minHeight: 60, '&.Mui-expanded': { minHeight: 60 } }}
      >
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={{ xs: 1, sm: 2 }}
          alignItems={{ xs: 'flex-start', sm: 'center' }}
          sx={{ width: '100%' }}
        >
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
              {communication.vendor_name}
            </Typography>
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
              {formatPhoneNumber(communication.phone_number)}
            </Typography>
          </Box>

          <Stack
            direction="row"
            spacing={0.5}
            alignItems="center"
            flexWrap="wrap"
            sx={{ flexShrink: 0 }}
          >
            <Chip
              icon={getCommunicationTypeIcon()}
              label={getCommunicationTypeLabel()}
              size="small"
              color={communication.type === 'sms' ? 'info' : 'primary'}
              variant="outlined"
              sx={{ height: 20, fontSize: '0.7rem' }}
            />
            {communication.has_quote && (
              <Chip
                icon={<CheckCircleIcon width={12} height={12} />}
                label="Quote"
                size="small"
                color="success"
                sx={{ height: 20, fontSize: '0.7rem' }}
              />
            )}
            {communication.type === 'call' &&
              'has_recording' in communication &&
              communication.has_recording && (
              <Chip
                icon={<Headphones01Icon width={12} height={12} />}
                label="Audio"
                size="small"
                color="info"
                variant="outlined"
                sx={{ height: 20, fontSize: '0.7rem' }}
              />
            )}
            <Chip
              label={getStatusLabel(communication.status)}
              size="small"
              color={getStatusColor(communication.status)}
              variant="outlined"
              sx={{ height: 20, fontSize: '0.7rem' }}
            />
          </Stack>
        </Stack>
      </AccordionSummary>

      <AccordionDetails sx={{ pt: 0 }}>
        <Stack spacing={2} my={2}>
          {/* Communication Summary - Most Important */}
          {(communication.type === 'call' &&
            'summarized_transcript' in communication &&
            communication.summarized_transcript) ||
          (communication.type === 'sms' &&
            'conversation_summary' in communication &&
            communication.conversation_summary) ? (
              <Paper
                sx={{
                  p: 2,
                  bgcolor: 'primary.50',
                  borderLeft: 3,
                  borderColor: 'primary.main',
                }}
                elevation={0}
              >
                <Typography
                  variant="caption"
                  sx={{
                    color: 'primary.main',
                    fontWeight: 600,
                    mb: 1,
                    display: 'block',
                  }}
                >
                  {communication.type === 'call' ? 'AI CALL SUMMARY' : 'SMS CONVERSATION SUMMARY'}
                </Typography>
                <ReactMarkdown components={markdownComponents}>
                  {(() => {
                    if (communication.type === 'call' && 'summarized_transcript' in communication) {
                      return communication.summarized_transcript;
                    }
                    if (communication.type === 'sms' && 'conversation_summary' in communication) {
                      return communication.conversation_summary;
                    }
                    return '';
                  })()}
                </ReactMarkdown>
              </Paper>
            ) : null}

          {/* SMS Conversation Content with Bubble Style */}
          {communication.type === 'sms' &&
            'message_content' in communication &&
            communication.message_content && (
            <Paper sx={{ p: 2, bgcolor: 'grey.50' }} elevation={0}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <MessageChatSquareIcon width={16} height={16} style={{ color: '#6B7280' }} />
                <Typography
                  variant="caption"
                  sx={{
                    color: 'text.secondary',
                    fontWeight: 600,
                  }}
                >
                    SMS CONVERSATION
                </Typography>
                {communication.message_content && (
                  <Chip
                    label={`${communication.message_content.split('\n\n').length} messages`}
                    size="small"
                    variant="outlined"
                    sx={{ height: 18, fontSize: '0.65rem' }}
                  />
                )}
              </Box>
              <Box sx={{ maxHeight: 300, overflowY: 'auto', px: 1 }}>
                <Stack spacing={1.5}>
                  {communication.message_content.split('\n\n').map((message, msgIndex) => {
                    // Parse message format: "Vendor A (outbound): message content"
                    const match = message.match(/^(.+?) \((outbound|inbound)\): (.+)$/);
                    if (!match) {
                      return (
                        <Box
                          key={`msg-${message.slice(0, 20).replace(/\s/g, '-')}`}
                          sx={{ textAlign: 'center', py: 1 }}
                        >
                          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                            {message}
                          </Typography>
                        </Box>
                      );
                    }

                    const [, sender, direction, content] = match;
                    const isOutbound = direction === 'outbound';
                    const isFromTerri = isOutbound || sender === 'Terri';

                    return (
                      <Box
                        key={`sms-${sender.replace(/\s/g, '-')}-${direction}-${content
                          .slice(0, 10)
                          .replace(/\s/g, '-')}`}
                        sx={{
                          display: 'flex',
                          justifyContent: isFromTerri ? 'flex-end' : 'flex-start',
                          mb: 1,
                        }}
                      >
                        <Box
                          sx={{
                            maxWidth: '75%',
                            p: 1.5,
                            borderRadius: isFromTerri
                              ? '16px 16px 4px 16px'
                              : '16px 16px 16px 4px',
                            bgcolor: isFromTerri ? 'primary.main' : 'white',
                            color: isFromTerri ? 'white' : 'text.primary',
                            border: isFromTerri ? 'none' : '1px solid',
                            borderColor: 'grey.300',
                            boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                            position: 'relative',
                            '&::before': isFromTerri
                              ? {
                                content: '""',
                                position: 'absolute',
                                bottom: 0,
                                right: -8,
                                width: 0,
                                height: 0,
                                border: '8px solid transparent',
                                borderTopColor: 'primary.main',
                                transform: 'rotate(45deg)',
                              }
                              : {
                                content: '""',
                                position: 'absolute',
                                bottom: 0,
                                left: -8,
                                width: 0,
                                height: 0,
                                border: '8px solid transparent',
                                borderTopColor: 'white',
                                transform: 'rotate(-45deg)',
                              },
                          }}
                        >
                          <Typography
                            variant="caption"
                            sx={{
                              color: isFromTerri ? 'rgba(255,255,255,0.8)' : 'text.secondary',
                              fontWeight: 600,
                              mb: 0.5,
                              display: 'block',
                            }}
                          >
                            {isFromTerri ? 'Terri' : sender}
                          </Typography>
                          <Typography
                            variant="body2"
                            sx={{
                              color: isFromTerri ? 'white' : 'text.primary',
                              lineHeight: 1.4,
                            }}
                          >
                            {content}
                          </Typography>
                          <Typography
                            variant="caption"
                            sx={{
                              color: isFromTerri ? 'rgba(255,255,255,0.6)' : 'text.secondary',
                              fontSize: '0.65rem',
                              mt: 0.5,
                              display: 'block',
                            }}
                          >
                            {formatMessageTimestamp(communication.created_at)}
                          </Typography>
                        </Box>
                      </Box>
                    );
                  })}
                </Stack>
              </Box>
            </Paper>
          )}

          {/* Audio Player for calls */}
          {communication.type === 'call' &&
            'has_recording' in communication &&
            communication.has_recording &&
            'audio_url' in communication &&
            communication.audio_url && (
            <Paper sx={{ p: 2, bgcolor: 'grey.50' }} elevation={0}>
              <Stack direction="row" alignItems="center" spacing={2}>
                <Headphones01Icon width={18} height={18} style={{ color: '#6B7280' }} />
                {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
                <audio controls style={{ width: '100%', height: '32px' }}>
                  <source src={communication.audio_url} type="audio/mpeg" />
                    Your browser does not support the audio element.
                </audio>
              </Stack>
            </Paper>
          )}

          {/* Quote Information - Show for all vendors */}
          {(() => {
            if (communication.quote_summary) {
              return (
                <Paper
                  sx={{
                    p: 2,
                    bgcolor: communication.quote_summary.available ? 'success.50' : 'grey.50',
                    borderLeft: 3,
                    borderColor: communication.quote_summary.available
                      ? 'success.main'
                      : 'grey.400',
                  }}
                  elevation={0}
                >
                  <Typography
                    variant="caption"
                    sx={{
                      color: communication.quote_summary.available
                        ? 'success.main'
                        : 'text.secondary',
                      fontWeight: 600,
                    }}
                  >
                    {communication.quote_summary.available ? 'QUOTE RECEIVED' : 'QUOTE INFORMATION'}
                  </Typography>
                  <Stack spacing={1} sx={{ mt: 1 }}>
                    {communication.quote_summary.price_range && (
                      <Box>
                        <Typography variant="caption" color="text.secondary">
                          Price Range:
                        </Typography>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {communication.quote_summary.price_range}
                        </Typography>
                      </Box>
                    )}

                    {communication.quote_summary.pricing_info &&
                      communication.quote_summary.pricing_info.length > 0 && (
                      <Box>
                        <Typography variant="caption" color="text.secondary">
                            Pricing Details:
                        </Typography>
                        <Stack spacing={0.5} sx={{ mt: 0.5 }}>
                          {communication.quote_summary.pricing_info.map(info => (
                            <Typography key={info} variant="body2" sx={{ pl: 1 }}>
                                • {info}
                            </Typography>
                          ))}
                        </Stack>
                      </Box>
                    )}

                    {communication.quote_summary.timeline && (
                      <Box>
                        <Typography variant="caption" color="text.secondary">
                          Timeline:
                        </Typography>
                        <Typography variant="body2">
                          {communication.quote_summary.timeline}
                        </Typography>
                      </Box>
                    )}

                    {communication.quote_summary.services_offered &&
                      communication.quote_summary.services_offered.length > 0 && (
                      <Box>
                        <Typography variant="caption" color="text.secondary">
                            Services Offered:
                        </Typography>
                        <Stack direction="row" spacing={0.5} flexWrap="wrap" sx={{ mt: 0.5 }}>
                          {communication.quote_summary.services_offered.map(service => (
                            <Chip
                              key={service}
                              label={service}
                              size="small"
                              sx={{ height: 22, fontSize: '0.75rem' }}
                            />
                          ))}
                        </Stack>
                      </Box>
                    )}

                    {communication.quote_summary.availability && (
                      <Box>
                        <Typography variant="caption" color="text.secondary">
                          Availability:
                        </Typography>
                        <Typography variant="body2">
                          {communication.quote_summary.availability}
                        </Typography>
                      </Box>
                    )}

                    {communication.quote_summary.requirements && (
                      <Box>
                        <Typography variant="caption" color="text.secondary">
                          Requirements:
                        </Typography>
                        <Typography variant="body2">
                          {communication.quote_summary.requirements}
                        </Typography>
                      </Box>
                    )}

                    {communication.quote_summary.follow_up_needed && (
                      <Box sx={{ mt: 1, p: 1, bgcolor: 'warning.50', borderRadius: 1 }}>
                        <Typography variant="body2" color="warning.main" sx={{ fontWeight: 600 }}>
                          ⚠️ Follow-up required with vendor
                        </Typography>
                      </Box>
                    )}
                  </Stack>
                </Paper>
              );
            }

            if (communication.has_quote) {
              // Fallback if quote_summary is missing but has_quote is true
              return (
                <Paper
                  sx={{
                    p: 2,
                    bgcolor: 'grey.50',
                    borderLeft: 3,
                    borderColor: 'grey.400',
                  }}
                  elevation={0}
                >
                  <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600 }}>
                    QUOTE STATUS
                  </Typography>
                  <Typography variant="body2" sx={{ mt: 1, color: 'text.secondary' }}>
                    Quote information available but details not loaded
                  </Typography>
                </Paper>
              );
            }

            return null;
          })()}

          {/* Communication Metadata - Compact */}
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={{ xs: 1, sm: 3 }}
            sx={{
              py: 1,
              px: 2,
              bgcolor: 'grey.50',
              borderRadius: 1,
            }}
          >
            <Box>
              <Typography variant="caption" color="text.secondary">
                Date
              </Typography>
              <Typography variant="body2">
                {format(new Date(communication.created_at), 'MMM d, h:mm a')}
              </Typography>
            </Box>
            {communication.urgency && (
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Urgency
                </Typography>
                <Typography variant="body2">{communication.urgency}</Typography>
              </Box>
            )}
            {communication.type === 'call' &&
              'is_voicemail' in communication &&
              communication.is_voicemail && (
              <Box>
                <Typography variant="caption" color="text.secondary">
                    Voicemail
                </Typography>
                <Typography variant="body2">
                  {'voicemail_message_left' in communication &&
                    communication.voicemail_message_left
                    ? 'Message left'
                    : 'No message'}
                </Typography>
              </Box>
            )}
            {communication.type === 'call' &&
              'call_ended_reason' in communication &&
              communication.call_ended_reason && (
              <Box>
                <Typography variant="caption" color="text.secondary">
                    Call End
                </Typography>
                <Typography variant="body2">{communication.call_ended_reason}</Typography>
              </Box>
            )}
            {communication.type === 'sms' && (
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Messages
                </Typography>
                <Typography variant="body2">
                  {communication.message_content
                    ? `${communication.message_content.split('\n\n').length} messages`
                    : 'SMS Thread'}
                </Typography>
              </Box>
            )}
          </Stack>

          {/* Requirements if present */}
          {communication.requirements && (
            <Box sx={{ px: 2, py: 1, bgcolor: 'grey.50', borderRadius: 1 }}>
              <CustomerRequirementsDisplay requirements={communication.requirements} />
            </Box>
          )}
        </Stack>
      </AccordionDetails>
    </Accordion>
  );
};

// Vendor Card Component
const VendorCard: React.FC<{ vendor: VendorDetails; index: number }> = ({ vendor, index }) => {
  const formatPhone = (phone: string) => {
    const cleaned = phone.replace(/\D/g, '');
    if (cleaned.length === 10) {
      return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
    }
    return phone;
  };

  const getMatchScoreColor = (score: number) => {
    if (score >= 0.8) return 'success';
    if (score >= 0.6) return 'warning';
    return 'default';
  };

  return (
    <Card
      sx={{
        height: '100%',
        transition: 'all 0.2s',
        '&:hover': {
          transform: 'translateY(-2px)',
          boxShadow: 3,
        },
      }}
      elevation={0}
    >
      <CardContent sx={{ p: 3 }}>
        <Stack spacing={2}>
          {/* Header */}
          <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flex: 1 }}>
              <Avatar
                sx={{
                  bgcolor: 'primary.main',
                  color: 'white',
                  width: 36,
                  height: 36,
                  fontSize: '0.9rem',
                  fontWeight: 600,
                }}
              >
                {index + 1}
              </Avatar>
              <Box sx={{ flex: 1 }}>
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 600,
                    color: 'text.primary',
                    fontSize: '1.1rem',
                  }}
                >
                  {vendor.name}
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                  {vendor.services}
                </Typography>
              </Box>
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 1 }}>
              <Chip
                label={`${(vendor.match_score * 100).toFixed(0)}% match`}
                size="small"
                color={getMatchScoreColor(vendor.match_score)}
                variant="outlined"
                sx={{ height: 22, fontSize: '0.75rem' }}
              />
              {vendor.emergency_service && (
                <Chip
                  label="Emergency"
                  size="small"
                  color="error"
                  variant="outlined"
                  sx={{ height: 22, fontSize: '0.75rem' }}
                />
              )}
            </Box>
          </Box>

          {/* Distance and Address */}
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
              <MarkerPin01Icon width={16} height={16} style={{ color: '#6B7280' }} />
              <Typography
                variant="body2"
                sx={{
                  color: 'text.secondary',
                  fontSize: '0.875rem',
                }}
              >
                {vendor.address}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: 3 }}>
              <Typography
                variant="body2"
                sx={{
                  color: 'primary.main',
                  fontWeight: 600,
                  fontSize: '0.875rem',
                }}
              >
                {vendor.distance_formatted || vendor.distance}
              </Typography>
              {vendor.distance_meters === null && (
                <Chip
                  label="Calculating..."
                  size="small"
                  color="info"
                  variant="outlined"
                  sx={{ height: 18, fontSize: '0.65rem' }}
                />
              )}
              {vendor.distance_meters !== null && (
                <Chip
                  label="Mapbox"
                  size="small"
                  color="success"
                  variant="outlined"
                  sx={{ height: 18, fontSize: '0.65rem' }}
                />
              )}
            </Box>
          </Box>

          {/* Recommendation Reason */}
          <Box
            sx={{
              bgcolor: 'grey.50',
              p: 1.5,
              borderRadius: 1,
              borderLeft: '3px solid',
              borderLeftColor: 'primary.main',
            }}
          >
            <Typography
              variant="body2"
              sx={{
                color: 'text.primary',
                fontStyle: 'italic',
                fontSize: '0.875rem',
              }}
            >
              "{vendor.reason_for_recommendation}"
            </Typography>
          </Box>

          {/* Contact Information */}
          <Divider />
          <Stack spacing={1}>
            <Typography
              variant="subtitle2"
              sx={{
                fontWeight: 600,
                color: 'text.primary',
                fontSize: '0.875rem',
              }}
            >
              Contact Information
            </Typography>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <PhoneIcon width={14} height={14} style={{ color: '#6B7280' }} />
              <MuiLink
                href={`tel:${vendor.phone}`}
                sx={{
                  textDecoration: 'none',
                  color: 'primary.main',
                  fontSize: '0.875rem',
                }}
              >
                {formatPhone(vendor.phone)}
              </MuiLink>
              <Chip
                label={vendor.phone_type}
                size="small"
                variant="outlined"
                sx={{ fontSize: '0.65rem', height: 18 }}
              />
            </Box>

            {vendor.email && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Mail01Icon width={14} height={14} style={{ color: '#6B7280' }} />
                <MuiLink
                  href={`mailto:${vendor.email}`}
                  sx={{
                    textDecoration: 'none',
                    color: 'primary.main',
                    fontSize: '0.875rem',
                  }}
                >
                  {vendor.email}
                </MuiLink>
              </Box>
            )}

            {vendor.website && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Globe01Icon width={14} height={14} style={{ color: '#6B7280' }} />
                <MuiLink
                  href={
                    vendor.website.startsWith('http') ? vendor.website : `https://${vendor.website}`
                  }
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{
                    textDecoration: 'none',
                    color: 'primary.main',
                    fontSize: '0.875rem',
                  }}
                >
                  {vendor.website}
                </MuiLink>
              </Box>
            )}
          </Stack>
        </Stack>
      </CardContent>
    </Card>
  );
};

const VendorSearchDetailPage: React.FC = () => {
  const history = useHistory();
  const { searchId } = useParams<{ searchId: string }>();
  const [activeTab, setActiveTab] = React.useState(0);

  // Get search history data which includes vendors
  const {
    data: searchHistory,
    isLoading: isLoadingHistory,
    error: historyError,
  } = useGetSearchHistory();

  // Find the specific search from history
  const searchDetails = React.useMemo(
    () => searchHistory?.find((search: VendorSearchHistory) => search.id === searchId),
    [searchHistory, searchId]
  );

  // Monitor distance calculation status
  const { data: distanceStatus } = useDistanceCalculationStatus(searchId, !!searchDetails);

  // Distance calculation trigger
  const triggerDistanceCalculation = useTriggerDistanceCalculation();

  // Fetch unified communication history (calls + SMS) for this search
  const {
    data: communicationHistory,
    isLoading: isLoadingCommunications,
    error: communicationError,
  } = useGetCommunicationHistory(
    searchDetails ? { searchQuery: searchDetails.search_query } : undefined,
    !!searchDetails
  );

  const handleBackToHistory = () => {
    history.push(PMPRoutes.vendorSearchHistory);
  };

  return (
    <Box sx={{ py: { xs: 2, md: 3 }, bgcolor: 'grey.50', minHeight: '100vh' }}>
      <Container maxWidth="lg">
        <Stack spacing={3}>
          {/* Enhanced Header Section */}
          <Paper
            sx={{
              p: { xs: 3, md: 4 },
              borderRadius: 3,
              background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
              border: '1px solid',
              borderColor: 'grey.200',
              boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
              position: 'relative',
              overflow: 'hidden',
              '&::before': {
                content: '""',
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                height: '4px',
                background: 'linear-gradient(90deg, #1976d2 0%, #42a5f5 100%)',
              },
            }}
            elevation={0}
          >
            {/* Back Button */}
            <Button
              startIcon={<ArrowLeftIcon width={18} height={18} />}
              onClick={handleBackToHistory}
              size="small"
              sx={{
                mb: 3,
                color: 'text.secondary',
                '&:hover': {
                  backgroundColor: 'rgba(25, 118, 210, 0.08)',
                  color: 'primary.main',
                },
              }}
            >
              Back to History
            </Button>

            {searchDetails && (
              <Stack spacing={3}>
                {/* Main Search Query */}
                <Box sx={{ textAlign: 'center' }}>
                  <Typography
                    variant="h4"
                    sx={{
                      fontWeight: 800,
                      color: 'text.primary',
                      fontSize: { xs: '1.75rem', md: '2.125rem' },
                      lineHeight: 1.2,
                      mb: 1,
                      textTransform: 'capitalize',
                    }}
                  >
                    {searchDetails.search_query}
                  </Typography>

                  {/* Processed Query */}
                  {searchDetails.processed_query &&
                    searchDetails.processed_query !== searchDetails.search_query && (
                    <Typography
                      variant="body1"
                      sx={{
                        color: 'text.secondary',
                        fontStyle: 'italic',
                        mb: 1,
                        fontSize: '0.95rem',
                      }}
                    >
                        Searched as: {searchDetails.processed_query}
                    </Typography>
                  )}

                  {/* Timestamp */}
                  <Typography
                    variant="body2"
                    sx={{
                      color: 'text.secondary',
                      fontSize: '0.875rem',
                      opacity: 0.8,
                    }}
                  >
                    Search performed{' '}
                    {searchDetails.created_at
                      ? format(new Date(searchDetails.created_at), 'MMM d, yyyy h:mm a')
                      : 'recently'}
                  </Typography>
                </Box>

                {/* Enhanced Metadata */}
                <Stack
                  direction={{ xs: 'column', sm: 'row' }}
                  spacing={2}
                  alignItems="center"
                  justifyContent="center"
                  sx={{ pt: 1 }}
                >
                  <Chip
                    icon={<MarkerPin01Icon width={16} height={16} />}
                    label={searchDetails.address}
                    size="medium"
                    sx={{
                      bgcolor: 'background.paper',
                      border: '1px solid',
                      borderColor: 'grey.300',
                      fontWeight: 500,
                      '& .MuiChip-label': {
                        fontSize: '0.875rem',
                      },
                    }}
                  />
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography
                      variant="body1"
                      sx={{
                        color: 'text.secondary',
                        fontWeight: 500,
                      }}
                    >
                      vendors found
                    </Typography>
                    <Badge
                      badgeContent={searchDetails.vendor_count}
                      color="primary"
                      sx={{
                        '& .MuiBadge-badge': {
                          position: 'static',
                          transform: 'none',
                          fontSize: '0.75rem',
                          fontWeight: 700,
                          minWidth: '24px',
                          height: '24px',
                          borderRadius: '12px',
                        },
                      }}
                    />
                  </Box>
                </Stack>
              </Stack>
            )}
          </Paper>

          {/* Distance Calculation Status */}
          {searchDetails && distanceStatus && !distanceStatus.calculation_complete && (
            <Paper
              sx={{
                p: 2,
                bgcolor: 'info.50',
                borderLeft: 3,
                borderColor: 'info.main',
              }}
              elevation={0}
            >
              <Stack spacing={2}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Loading01Icon width={20} height={20} style={{ color: '#0EA5E9' }} />
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'info.main' }}>
                    Calculating Accurate Distances
                  </Typography>
                  <Chip
                    label={`${distanceStatus.calculated_distances}/${distanceStatus.total_vendors} complete`}
                    size="small"
                    color="info"
                    variant="outlined"
                  />
                </Box>

                <Box>
                  <LinearProgress
                    variant="determinate"
                    value={
                      (distanceStatus.calculated_distances / distanceStatus.total_vendors) * 100
                    }
                    sx={{
                      height: 6,
                      borderRadius: 3,
                      bgcolor: 'info.100',
                      '& .MuiLinearProgress-bar': {
                        bgcolor: 'info.main',
                      },
                    }}
                  />
                  <Typography
                    variant="caption"
                    sx={{ color: 'text.secondary', mt: 1, display: 'block' }}
                  >
                    We're calculating precise driving distances using Mapbox. Vendor ordering will
                    improve as calculations complete.
                  </Typography>
                </Box>

                {distanceStatus.pending_calculations > 0 && (
                  <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() => triggerDistanceCalculation.mutate(searchId)}
                      disabled={triggerDistanceCalculation.isPending}
                      sx={{ fontSize: '0.75rem' }}
                    >
                      {triggerDistanceCalculation.isPending ? 'Calculating...' : 'Recalculate'}
                    </Button>
                  </Box>
                )}
              </Stack>
            </Paper>
          )}

          {/* Summary Stats - Moved to top for immediate visibility */}
          {communicationHistory && communicationHistory.total_count > 0 && (
            <Paper sx={{ p: 2, borderRadius: 2 }} elevation={0}>
              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                spacing={{ xs: 2, sm: 0 }}
                divider={<Divider orientation="vertical" flexItem />}
                alignItems="center"
                justifyContent="space-around"
              >
                <Box sx={{ textAlign: 'center', px: 2 }}>
                  <Typography variant="h5" sx={{ fontWeight: 700, color: 'primary.main' }}>
                    {communicationHistory.total_count}
                  </Typography>
                  <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                    Total Communications
                  </Typography>
                </Box>
                <Box sx={{ textAlign: 'center', px: 2 }}>
                  <Typography variant="h5" sx={{ fontWeight: 700, color: 'success.main' }}>
                    {communicationHistory.call_count}
                  </Typography>
                  <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                    Calls
                  </Typography>
                </Box>
                <Box sx={{ textAlign: 'center', px: 2 }}>
                  <Typography variant="h5" sx={{ fontWeight: 700, color: 'info.main' }}>
                    {communicationHistory.sms_count}
                  </Typography>
                  <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                    SMS
                  </Typography>
                </Box>
                <Box sx={{ textAlign: 'center', px: 2 }}>
                  <Typography variant="h5" sx={{ fontWeight: 700, color: 'warning.main' }}>
                    {communicationHistory.quote_count}
                  </Typography>
                  <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                    Quotes
                  </Typography>
                </Box>
              </Stack>
            </Paper>
          )}

          {/* Enhanced Tabs Section */}
          <Paper
            sx={{
              borderRadius: 3,
              overflow: 'hidden',
              boxShadow: '0 2px 12px rgba(0, 0, 0, 0.08)',
            }}
            elevation={0}
          >
            <Box sx={{ bgcolor: 'grey.50', px: 1, py: 1 }}>
              <Tabs
                value={activeTab}
                onChange={(_, newValue) => setActiveTab(newValue)}
                sx={{
                  minHeight: 'auto',
                  '& .MuiTabs-flexContainer': {
                    gap: 1,
                  },
                  '& .MuiTabs-indicator': {
                    display: 'none',
                  },
                }}
                TabIndicatorProps={{ style: { display: 'none' } }}
              >
                <Tab
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 0.5, px: 1 }}>
                      <MessageChatSquareIcon width={18} height={18} />
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        Communications
                      </Typography>
                      {communicationHistory && communicationHistory.total_count > 0 && (
                        <Chip
                          label={communicationHistory.total_count}
                          size="small"
                          sx={{
                            height: 20,
                            fontSize: '0.7rem',
                            fontWeight: 700,
                            bgcolor: activeTab === 0 ? 'primary.main' : 'grey.300',
                            color: activeTab === 0 ? 'white' : 'text.secondary',
                          }}
                        />
                      )}
                    </Box>
                  }
                  sx={{
                    textTransform: 'none',
                    minHeight: 'auto',
                    py: 1,
                    px: 2,
                    borderRadius: 2,
                    bgcolor: activeTab === 0 ? 'white' : 'transparent',
                    color: activeTab === 0 ? 'primary.main' : 'text.secondary',
                    boxShadow: activeTab === 0 ? '0 2px 8px rgba(0, 0, 0, 0.1)' : 'none',
                    '&:hover': {
                      bgcolor: activeTab === 0 ? 'white' : 'rgba(255, 255, 255, 0.7)',
                    },
                    transition: 'all 0.2s ease-in-out',
                  }}
                />
                <Tab
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 0.5, px: 1 }}>
                      <MarkerPin01Icon width={18} height={18} />
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        Vendors
                      </Typography>
                      {searchDetails?.vendors && (
                        <Chip
                          label={searchDetails.vendors.length}
                          size="small"
                          sx={{
                            height: 20,
                            fontSize: '0.7rem',
                            fontWeight: 700,
                            bgcolor: activeTab === 1 ? 'primary.main' : 'grey.300',
                            color: activeTab === 1 ? 'white' : 'text.secondary',
                          }}
                        />
                      )}
                    </Box>
                  }
                  sx={{
                    textTransform: 'none',
                    minHeight: 'auto',
                    py: 1,
                    px: 2,
                    borderRadius: 2,
                    bgcolor: activeTab === 1 ? 'white' : 'transparent',
                    color: activeTab === 1 ? 'primary.main' : 'text.secondary',
                    boxShadow: activeTab === 1 ? '0 2px 8px rgba(0, 0, 0, 0.1)' : 'none',
                    '&:hover': {
                      bgcolor: activeTab === 1 ? 'white' : 'rgba(255, 255, 255, 0.7)',
                    },
                    transition: 'all 0.2s ease-in-out',
                  }}
                />
              </Tabs>
            </Box>
          </Paper>

          {/* Tab Content */}
          {activeTab === 0 && (
            /* Unified Communication History Section */
            <Paper sx={{ p: 3, borderRadius: 3, mt: 2, bgcolor: 'white' }} elevation={0}>
              <Stack spacing={3}>
                {isLoadingCommunications && (
                  <Box sx={{ textAlign: 'center', py: 4 }}>
                    <CircularProgress size={32} />
                    <Typography variant="body1" sx={{ mt: 2, color: 'text.secondary' }}>
                      Loading communication history...
                    </Typography>
                  </Box>
                )}

                {communicationError && (
                  <Alert severity="error" variant="outlined" sx={{ borderRadius: 2 }}>
                    Failed to load communication history. Please try again later.
                  </Alert>
                )}

                {!isLoadingCommunications &&
                  !communicationError &&
                  (!communicationHistory ||
                    (communicationHistory && communicationHistory.communications.length === 0)) && (
                  <Box sx={{ textAlign: 'center', py: 6 }}>
                    <MessageChatSquareIcon width={48} height={48} style={{ color: '#D1D5DB' }} />
                    <Typography
                      variant="h6"
                      sx={{ mt: 2, color: 'text.secondary', fontWeight: 600 }}
                    >
                        No communications yet
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{ mt: 1, color: 'text.secondary', maxWidth: 400, mx: 'auto' }}
                    >
                        AI calls and SMS conversations will appear here once vendors are contacted
                        for this search query.
                    </Typography>
                  </Box>
                )}

                {communicationHistory && communicationHistory.communications.length > 0 && (
                  <Stack spacing={1.5}>
                    {communicationHistory.communications.map((communication, index) => (
                      <CommunicationItem
                        key={communication.id}
                        communication={communication}
                        index={index}
                      />
                    ))}
                  </Stack>
                )}
              </Stack>
            </Paper>
          )}

          {activeTab === 1 && (
            /* Vendors Section */
            <Paper sx={{ p: 3, borderRadius: 3, mt: 2, bgcolor: 'white' }} elevation={0}>
              <Stack spacing={3}>
                {/* Header with sort and filter options */}
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    flexWrap: 'wrap',
                    gap: 2,
                  }}
                >
                  <Box>
                    <Typography
                      variant="h6"
                      sx={{ fontWeight: 600, color: 'text.primary', mb: 0.5 }}
                    >
                      Recommended Vendors
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                      {searchDetails?.vendors?.length || 0} vendors found, sorted by relevance and
                      distance
                    </Typography>
                  </Box>
                  {searchDetails?.vendors && searchDetails.vendors.length > 0 && (
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Chip
                        icon={<CheckCircleIcon width={14} height={14} />}
                        label={`${
                          searchDetails.vendors.filter(v => v.match_score >= 0.8).length
                        } high match`}
                        size="small"
                        color="success"
                        variant="outlined"
                        sx={{ fontSize: '0.75rem' }}
                      />
                      <Chip
                        icon={<MarkerPin01Icon width={14} height={14} />}
                        label="Distance sorted"
                        size="small"
                        color="primary"
                        variant="outlined"
                        sx={{ fontSize: '0.75rem' }}
                      />
                    </Stack>
                  )}
                </Box>

                {isLoadingHistory && (
                  <Box sx={{ textAlign: 'center', py: 4 }}>
                    <CircularProgress size={32} />
                    <Typography variant="body1" sx={{ mt: 2, color: 'text.secondary' }}>
                      Loading vendor details...
                    </Typography>
                  </Box>
                )}

                {historyError && (
                  <Alert severity="error" variant="outlined" sx={{ borderRadius: 2 }}>
                    Failed to load vendor details. Please try again later.
                  </Alert>
                )}

                {searchDetails && searchDetails.vendors && searchDetails.vendors.length === 0 && (
                  <Box sx={{ textAlign: 'center', py: 6 }}>
                    <MarkerPin01Icon width={48} height={48} style={{ color: '#D1D5DB' }} />
                    <Typography
                      variant="h6"
                      sx={{ mt: 2, color: 'text.secondary', fontWeight: 600 }}
                    >
                      No vendors found
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{ mt: 1, color: 'text.secondary', maxWidth: 400, mx: 'auto' }}
                    >
                      Try adjusting your search criteria or expand your search area to find more
                      vendors.
                    </Typography>
                  </Box>
                )}

                {searchDetails && searchDetails.vendors && searchDetails.vendors.length > 0 && (
                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: {
                        xs: '1fr',
                        md: 'repeat(2, 1fr)',
                        lg: 'repeat(2, 1fr)',
                      },
                      gap: 3,
                    }}
                  >
                    {searchDetails.vendors.map((vendor, index) => (
                      <VendorCard key={vendor.id} vendor={vendor} index={index} />
                    ))}
                  </Box>
                )}
              </Stack>
            </Paper>
          )}
        </Stack>
      </Container>
    </Box>
  );
};

export default VendorSearchDetailPage;
